import { api } from "@/lib/api";

export type TenantBranding = {
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  sidebarBgColor: string | null;
  customUiColors: boolean;
};

export type ChainHospital = {
  id: string;
  name: string;
  /** 6-char unit code; null = legacy numbering (first site / Rudram). */
  unitCode?: string | null;
  address?: string;
  contactNumber?: string;
  email?: string;
  gstin?: string | null;
  /** Google Maps share link — WhatsApp directions + GMB lookup fallback. */
  mapsUrl?: string | null;
  /** Google Place ID (ChIJ…) for Business Reports GMB rating. */
  googlePlaceId?: string | null;
  /** IPD feedback Google Sheet link for Customer Voice / NPS. */
  ipdFeedbackSheetUrl?: string | null;
  /** Post-discharge surgery support contacts (IPD discharge summary PDF). */
  postSurgeryContact1Name?: string | null;
  postSurgeryContact1Phone?: string | null;
  postSurgeryContact2Name?: string | null;
  postSurgeryContact2Phone?: string | null;
  status?: string;
  logoUrl?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  sidebarBgColor?: string | null;
  customUiColors?: boolean | null;
};

export type ChainAdminRow = {
  id: string;
  name: string;
  email: string;
  adminScope: "CHAIN" | "HOSPITAL";
  status: string;
  hospitalId: string | null;
  hospital?: { id: string; name: string } | null;
};

export const chainApi = {
  getGroup: () => api.get("/api/hospital-admin/chain/group"),
  getDashboard: () => api.get("/api/hospital-admin/chain/dashboard"),
  listHospitals: () => api.get("/api/hospital-admin/chain/hospitals"),
  suggestUnitCode: (name: string, excludeHospitalId?: string) =>
    api.get("/api/hospital-admin/chain/hospitals/suggest-unit-code", {
      params: {
        name,
        ...(excludeHospitalId ? { excludeHospitalId } : {}),
      },
    }),
  createHospital: (body: Record<string, string | undefined>) =>
    api.post("/api/hospital-admin/chain/hospitals", body),
  updateHospital: (hospitalId: string, body: Record<string, unknown>) =>
    api.patch(`/api/hospital-admin/chain/hospitals/${hospitalId}`, body),
  listAdmins: () => api.get("/api/hospital-admin/chain/admins"),
  createAdmin: (body: Record<string, string>) =>
    api.post("/api/hospital-admin/chain/admins", body),
  updateAdmin: (
    adminId: string,
    body: {
      name?: string;
      email?: string;
      password?: string;
      adminScope?: "CHAIN" | "HOSPITAL";
      hospitalId?: string | null;
      status?: "ACTIVE" | "INACTIVE";
    }
  ) => api.patch(`/api/hospital-admin/chain/admins/${adminId}`, body),
  moveAdminHospital: (adminId: string, hospitalId: string) =>
    api.patch(`/api/hospital-admin/chain/admins/${adminId}/hospital`, { hospitalId }),
  switchHospital: (hospitalId: string) =>
    api.post("/api/hospital-admin/chain/context/hospital", { hospitalId }),
  getBranding: (hospitalId?: string) =>
    api.get("/api/hospital-admin/tenant/branding", {
      params: hospitalId ? { hospitalId } : undefined,
    }),
  uploadHospitalLogo: (hospitalId: string, file: File) => {
    const fd = new FormData();
    fd.append("logo", file);
    return api.post("/api/hospital-admin/tenant/branding/logo", fd, {
      params: { hospitalId },
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const tenantApi = {
  listGroups: () => api.get("/api/super-admin/groups"),
  createGroup: (body: Record<string, unknown>) =>
    api.post("/api/super-admin/groups", body),
  updateGroup: (id: string, body: Record<string, unknown>) =>
    api.patch(`/api/super-admin/groups/${id}`, body),
  uploadGroupLogo: (groupId: string, file: File) => {
    const fd = new FormData();
    fd.append("logo", file);
    return api.post(`/api/super-admin/groups/${groupId}/branding/logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};
